import * as React from 'react';
import * as z from 'zod';
import { useLingui } from '@lingui/react/macro';
import type { PortalLocale } from '@worldcoo/monolith/portals';

// Per-locale subpaths: the 'zod/locales' barrel bundles every locale into one chunk.
const zodLocaleMap: Record<string, () => Promise<{ localeError: z.core.$ZodErrorMap }>> = {
	ca: () => import('zod/v4/locales/ca.js').then((m) => m.default()),
	de: () => import('zod/v4/locales/de.js').then((m) => m.default()),
	en: () => import('zod/v4/locales/en.js').then((m) => m.default()),
	es: () => import('zod/v4/locales/es.js').then((m) => m.default()),
	eu: () => import('zod/v4/locales/es.js').then((m) => m.default()), // Basque: fallback to Spanish
	fr: () => import('zod/v4/locales/fr.js').then((m) => m.default()),
	gl: () => import('zod/v4/locales/es.js').then((m) => m.default()), // Galician: fallback to Spanish
	it: () => import('zod/v4/locales/it.js').then((m) => m.default()),
	nl: () => import('zod/v4/locales/nl.js').then((m) => m.default()),
	pt: () => import('zod/v4/locales/pt.js').then((m) => m.default()),
};

export function useZodI18n() {
	const { t, i18n } = useLingui();
	const locale = (i18n.locale || 'en') as PortalLocale;

	React.useEffect(() => {
		const loadLocale = zodLocaleMap[locale] ?? zodLocaleMap['en']!;
		loadLocale().then((zodLocale) => {
			z.config({
				...zodLocale,
				customError: (issue) => {
					switch (issue.code) {
						case 'too_small': {
							if ((issue.origin === 'string' || issue.origin === 'array') && issue.minimum === 1) return t`Required`;
							const min = String(issue.minimum);
							if (issue.origin === 'string') return t`Minimum ${min} characters`;
							if (issue.origin === 'number' || issue.origin === 'int') return t`Minimum ${min}`;
							return undefined;
						}
						case 'too_big': {
							const max = String(issue.maximum);
							if (issue.origin === 'string') return t`Maximum ${max} characters`;
							if (issue.origin === 'number' || issue.origin === 'int') return t`Maximum ${max}`;
							if (issue.origin === 'file') {
								const bytes = Number(issue.maximum);
								const inGB = bytes / 1024 / 1024 / 1024;
								const inMB = bytes / 1024 / 1024;
								const size = inGB >= 1 ? `${inGB} GB` : `${inMB} MB`;
								return t`File must not exceed ${size}`;
							}
							if (issue.origin === 'array') return t`Maximum ${max} items`;
							return undefined;
						}
						case 'invalid_type': {
							if (
								issue.expected === 'string' ||
								issue.expected === 'nonoptional' ||
								issue.expected === 'number' ||
								issue.expected === 'file'
							)
								return t`Required`;
							return undefined;
						}
						case 'invalid_format': {
							if (issue.format === 'email') return t`Invalid email`;
							if (issue.format === 'url') return t`Invalid URL`;
							if (issue.format === 'date' || issue.format === 'datetime') return t`Invalid date`;
							return undefined;
						}
						case 'custom': {
							const key = (issue.params as { i18n?: string } | undefined)?.i18n;
							switch (key) {
								case 'required':
									return t`Required`;
								case 'emailsDoNotMatch':
									return t`Email addresses do not match.`;
								case 'termsAndConditions':
									return t`You must accept the terms and conditions`;
								case 'invalidIban':
									return t`Invalid IBAN`;
								case 'invalidSwift':
									return t`Invalid SWIFT/BIC`;
								case 'requiredWhenCertifiedByFoundation':
									return t`Required when certified by foundation`;
								case 'requiredWhenCorporateVolunteering':
									return t`Required when corporate volunteering is enabled`;
								case 'endDateBeforeStartDate':
									return t`End date must be after the start date`;
								default:
									return undefined;
							}
						}
						default:
							return undefined;
					}
				},
			});
		});
	}, [t, locale]);
}
